'use client';

import { useState, useMemo } from 'react';
import { Search, Plus, Eye, Edit2, Target, Calendar, Building, TrendingUp } from 'lucide-react';
import { DataTable } from '../../../components/shared/DataTable';
import { PageHeader } from '../../../components/shared/PageHeader';
import { Modal, FormField, FormInput, FormSelect, FormTextarea, Button } from '../../../components/shared/Modal';
import { opportunities, customers } from '../data/mock-data';
import { formatCurrency } from '../../../types';
import {
  opportunityStatusConfig,
  opportunityStageConfig,
  type Opportunity,
  type OpportunityStatus,
  leadSourceConfig,
} from '../types';

// 기회 등록 모달
function OpportunityAddModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="영업기회 등록"
      description="새로운 영업 기회를 등록합니다."
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button onClick={onClose}>등록</Button>
        </>
      }
    >
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <FormField label="기회번호" required>
            <FormInput placeholder="자동 생성" disabled />
          </FormField>
          <FormField label="기회명" required>
            <FormInput placeholder="기회명을 입력하세요" />
          </FormField>
          <FormField label="고객" required>
            <FormSelect>
              <option value="">고객을 선택하세요</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>{customer.name}</option>
              ))}
            </FormSelect>
          </FormField>
          <FormField label="영업 단계">
            <FormSelect>
              <option value="">선택하세요</option>
              {Object.entries(opportunityStageConfig).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </FormSelect>
          </FormField>
          <FormField label="예상 금액" required>
            <FormInput type="number" placeholder="0" />
          </FormField>
          <FormField label="성공 확률 (%)">
            <FormInput type="number" min={0} max={100} placeholder="0" />
          </FormField>
          <FormField label="예상 종료일" required>
            <FormInput type="date" />
          </FormField>
          <FormField label="리드 소스">
            <FormSelect>
              <option value="">선택하세요</option>
              {Object.entries(leadSourceConfig).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </FormSelect>
          </FormField>
          <FormField label="담당자">
            <FormSelect>
              <option value="">선택하세요</option>
              <option value="u001">김영업</option>
              <option value="u002">이마케팅</option>
              <option value="u003">박관리</option>
            </FormSelect>
          </FormField>
        </div>
        <FormField label="설명">
          <FormTextarea rows={3} placeholder="영업 기회에 대한 설명을 입력하세요" />
        </FormField>
      </div>
    </Modal>
  );
}

export function OpportunityListPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filteredData = useMemo(() => {
    return opportunities.filter((opp) => {
      const matchesSearch =
        opp.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (opp.customerName && opp.customerName.toLowerCase().includes(searchQuery.toLowerCase())) ||
        opp.code.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus = selectedStatus === 'all' || opp.status === selectedStatus;

      return matchesSearch && matchesStatus;
    });
  }, [searchQuery, selectedStatus]);

  // 상태별 통계
  const statusStats = useMemo(() => {
    const stats: Record<string, { count: number; value: number }> = {};
    opportunities.forEach((opp) => {
      if (!stats[opp.status]) {
        stats[opp.status] = { count: 0, value: 0 };
      }
      stats[opp.status].count += 1;
      stats[opp.status].value += opp.expectedAmount;
    });
    return stats;
  }, []);

  // 파이프라인 가치
  const pipelineValue = useMemo(() => {
    return opportunities
      .filter((o) => o.status !== 'won' && o.status !== 'lost')
      .reduce((sum, o) => sum + o.expectedAmount * (o.probability / 100), 0);
  }, []);

  const getStatusColor = (color: string) => {
    const colors: Record<string, string> = {
      blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      yellow: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
      green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    };
    return colors[color] || colors.blue;
  };

  const columns = [
    {
      key: 'code',
      label: '기회번호',
      width: '10%',
      sortable: true,
      render: (value: unknown) => (
        <span className="font-mono text-sm text-primary whitespace-nowrap">{String(value)}</span>
      ),
    },
    {
      key: 'name',
      label: '영업기회',
      width: '22%',
      sortable: true,
      render: (value: unknown, row: Opportunity) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
            <Target className="w-4 h-4 text-purple-600 dark:text-purple-400" />
          </div>
          <div className="min-w-0">
            <div className="font-medium truncate">{String(value)}</div>
            {row.customerName && (
              <div className="text-xs text-muted-foreground flex items-center gap-1">
                <Building className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{row.customerName}</span>
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'stage',
      label: '단계',
      width: '10%',
      render: (value: unknown) => {
        const stageConfig = opportunityStageConfig[value as keyof typeof opportunityStageConfig];
        return <span className="text-sm whitespace-nowrap">{stageConfig?.label || String(value)}</span>;
      },
    },
    {
      key: 'probability',
      label: '확률',
      width: '12%',
      render: (value: unknown) => (
        <div className="flex items-center gap-2">
          <div className="w-12 h-1.5 bg-muted rounded-full overflow-hidden flex-shrink-0">
            <div
              className="h-full bg-primary rounded-full"
              style={{ width: `${Number(value)}%` }}
            />
          </div>
          <span className="text-sm whitespace-nowrap">{String(value)}%</span>
        </div>
      ),
    },
    {
      key: 'expectedAmount',
      label: '예상 금액',
      width: '14%',
      align: 'right' as const,
      sortable: true,
      render: (value: unknown) => (
        <span className="whitespace-nowrap">{formatCurrency(value as number)}</span>
      ),
    },
    {
      key: 'expectedCloseDate',
      label: '예상 종료',
      width: '10%',
      sortable: true,
      render: (value: unknown) => (
        <div className="flex items-center gap-1 text-sm whitespace-nowrap">
          <Calendar className="w-3 h-3 text-muted-foreground flex-shrink-0" />
          {String(value)}
        </div>
      ),
    },
    {
      key: 'status',
      label: '상태',
      width: '8%',
      render: (value: unknown) => {
        const status = value as OpportunityStatus;
        const config = opportunityStatusConfig[status];
        return (
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(config.color)}`}>
            {config.label}
          </span>
        );
      },
    },
    {
      key: 'actions',
      label: '',
      width: '8%',
      render: () => (
        <div className="flex items-center gap-1">
          <button className="p-1.5 rounded hover:bg-accent" title="보기">
            <Eye className="w-4 h-4 text-muted-foreground" />
          </button>
          <button className="p-1.5 rounded hover:bg-accent" title="수정">
            <Edit2 className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="영업기회 관리"
        description="영업 기회를 조회하고 관리합니다."
        actions={
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" />
            기회 등록
          </button>
        }
      />

      {/* 파이프라인 요약 */}
      <div className="p-4 rounded-lg bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/20">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-muted-foreground">파이프라인 가치 (가중)</div>
            <div className="text-3xl font-bold mt-1">{formatCurrency(pipelineValue)}</div>
          </div>
          <TrendingUp className="w-10 h-10 text-purple-500/50" />
        </div>
      </div>

      {/* 상태별 요약 */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {Object.entries(opportunityStatusConfig).map(([status, config]) => {
          const stat = statusStats[status] || { count: 0, value: 0 };
          return (
            <button
              key={status}
              onClick={() => setSelectedStatus(selectedStatus === status ? 'all' : status)}
              className={`p-3 rounded-lg border text-left transition-colors ${
                selectedStatus === status
                  ? 'border-primary bg-primary/5'
                  : 'border-border bg-card hover:bg-accent/50'
              }`}
            >
              <div className="text-xs text-muted-foreground">{config.label}</div>
              <div className="text-xl font-bold mt-1">{stat.count}건</div>
              <div className="text-xs text-muted-foreground mt-1">
                {formatCurrency(stat.value)}
              </div>
            </button>
          );
        })}
      </div>

      {/* 필터 */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="기회번호, 이름, 고객 검색..."
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">전체 상태</option>
          {Object.entries(opportunityStatusConfig).map(([status, config]) => (
            <option key={status} value={status}>
              {config.label}
            </option>
          ))}
        </select>
      </div>

      {/* 데이터 테이블 */}
      <DataTable
        data={filteredData}
        columns={columns}
        pageSize={10}
        emptyMessage="검색 결과가 없습니다."
      />

      {/* 기회 등록 모달 */}
      <OpportunityAddModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}
